import React, { useCallback, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  LiveSpeechStageScreen,
  type LiveSpeechStageScreenHandle,
} from "../live-speech-stage-screen";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "./styles";

interface LiveSpeechStageDialogProps {
  open: boolean;
  onClose: () => void;
  auth: { [key: string]: any };
  additionalMessageProps: { [key: string]: any };
  title?: string;
}

export const LiveSpeechStageDialog: React.FC<LiveSpeechStageDialogProps> = ({
  open,
  onClose,
  auth,
  additionalMessageProps,
  title = "Simulation",
}) => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const screenRef = useRef<LiveSpeechStageScreenHandle>(null);
  const [isExiting, setIsExiting] = useState(false);

  const requestFinishAndClose = useCallback(async () => {
    if (isExiting) return;
    setIsExiting(true);
    try {
      if (screenRef.current) {
        // Awaits stop-and-flush; safeExit invokes onClose on completion.
        await screenRef.current.finish();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to finish live speech session safely:", error);
      onClose();
    } finally {
      setIsExiting(false);
    }
  }, [isExiting, onClose]);

  const handleClose = (_event?: {}, reason?: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    void requestFinishAndClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      disableEscapeKeyDown
      PaperProps={{
        sx: dialogPaperSx,
      }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.875rem", sm: "1rem" },
            "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
              {
                fontSize: "0.75rem",
              },
          }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={() => void requestFinishAndClose()}
          disabled={isExiting}
          aria-label="Close"
          sx={{
            color: "text.primary",
            padding: { xs: 0.5, sm: 1 },
            "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
              {
                padding: 0.25,
              },
          }}
          size={isMdDown ? "small" : "medium"}
        >
          <CloseIcon
            fontSize={isMdDown ? "small" : "medium"}
            sx={{
              "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                {
                  fontSize: "1rem",
                },
            }}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        {open && (
          <LiveSpeechStageScreen
            ref={screenRef}
            auth={auth}
            additionalMessageProps={additionalMessageProps}
            onConnectionClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
